import { memo, useEffect, useRef, useState } from "react";
import {
  EllipsisHorizontalIcon,
  BuildingStorefrontIcon,
  ArrowPathIcon,
  useCharity,
  useRevalidateAll,
} from "@";
import { useThemeStore } from "@/shared/stores/useThemeStore";
import { THEME_OPTIONS } from "./ThemeSwitcher";

/**
 * Mobile-only overflow menu (sm:hidden). Collects the secondary navbar
 * utilities — theme, cache revalidation, store link — that would otherwise
 * crowd the action cluster on small screens. On sm+ these show inline and
 * this button is hidden.
 */
const MoreMenu = () => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);
  const { theme, setTheme } = useThemeStore();
  const { charityStoreUrl } = useCharity();
  const { canRevalidate, confirmAndRevalidate, isRevalidating } =
    useRevalidateAll();

  // Close on outside click / Escape
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div ref={containerRef} className="relative flex items-center sm:hidden">
      <button
        type="button"
        aria-haspopup="true"
        aria-expanded={open}
        aria-label="المزيد"
        onClick={() => setOpen((v) => !v)}
        className={`group relative flex h-10 w-10 items-center justify-center rounded-full ring-1 transition-all duration-200 active:scale-95 ${
          open
            ? "bg-purple-50 text-purple-900 ring-purple-200 dark:bg-purple-900/40 dark:text-purple-300 dark:ring-purple-700/50"
            : "bg-white text-gray-500 ring-gray-200 hover:bg-purple-50 hover:text-purple-900 hover:ring-purple-200 dark:bg-gray-900 dark:text-gray-300 dark:ring-gray-700 dark:hover:bg-gray-900/50 dark:hover:text-purple-300 dark:hover:ring-purple-700/40"
        }`}
      >
        <EllipsisHorizontalIcon className="h-5 w-5" />
      </button>

      {open && (
        <div
          role="menu"
          aria-label="إجراءات إضافية"
          className="absolute end-0 top-full z-[60] mt-2 w-72 overflow-hidden rounded-2xl border border-gray-100 bg-white/95 shadow-xl shadow-gray-900/10 backdrop-blur-xl [animation:fadeIn_160ms_ease-out_both] dark:border-gray-700/60 dark:bg-gray-900/95 dark:shadow-black/40"
        >
          {/* Theme selector */}
          <div className="border-b border-gray-100 p-2 dark:border-gray-800">
            <p className="px-2 pb-1.5 pt-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-purple-700 dark:text-purple-400">
              المظهر
            </p>
            <div className="grid grid-cols-3 gap-1">
              {THEME_OPTIONS.map(({ value, label, Icon }) => {
                const active = theme === value;
                return (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setTheme(value)}
                    aria-pressed={active}
                    title={label}
                    className={`flex flex-col items-center gap-1 rounded-xl px-2 py-2 text-[11px] font-medium tracking-tight transition-colors ${
                      active
                        ? "bg-purple-50 text-purple-900 ring-1 ring-purple-200 dark:bg-purple-950/40 dark:text-purple-300 dark:ring-purple-800/50"
                        : "text-gray-600 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-800"
                    }`}
                  >
                    <Icon className="h-[18px] w-[18px]" />
                    {label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Actions */}
          <ul className="p-2" role="none">
            {canRevalidate && (
              <li role="none">
                <button
                  type="button"
                  role="menuitem"
                  disabled={isRevalidating}
                  onClick={() => {
                    setOpen(false);
                    confirmAndRevalidate();
                  }}
                  className="group/item flex w-full items-center gap-3 rounded-2xl px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-purple-50 hover:text-purple-900 disabled:cursor-not-allowed disabled:opacity-60 dark:text-gray-300 dark:hover:bg-gray-800 dark:hover:text-purple-300"
                >
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gray-50 text-gray-500 ring-1 ring-gray-100 dark:bg-gray-800 dark:text-gray-400 dark:ring-gray-700">
                    <ArrowPathIcon
                      className={`h-[18px] w-[18px] ${
                        isRevalidating ? "animate-spin" : ""
                      }`}
                    />
                  </span>
                  <span className="flex-1 text-start tracking-tight">
                    تحديث كاش المنصة
                  </span>
                </button>
              </li>
            )}

            {charityStoreUrl && (
              <li role="none">
                <a
                  role="menuitem"
                  href={charityStoreUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setOpen(false)}
                  className="group/item flex w-full items-center gap-3 rounded-2xl px-3 py-2.5 text-sm font-medium text-gray-700 transition-colors hover:bg-purple-50 hover:text-purple-900 dark:text-gray-300 dark:hover:bg-gray-800 dark:hover:text-purple-300"
                >
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gray-50 text-gray-500 ring-1 ring-gray-100 dark:bg-gray-800 dark:text-gray-400 dark:ring-gray-700">
                    <BuildingStorefrontIcon className="h-[18px] w-[18px]" />
                  </span>
                  <span className="flex-1 text-start tracking-tight">
                    عرض المنصة
                  </span>
                </a>
              </li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

export default memo(MoreMenu);
